import { createRouter, createWebHistory } from 'vue-router';
import { updateLayout } from '@/router/guard/UpdateLayout';
import { isEmpty, memorizeScroll, recoverScroll } from '@/common/utils/Util';
import store from '@/store';
import MainPath from '@/router/path/MainPath';
import UserPath from '@/router/path/UserPath';
import FuneralPath from '@/router/path/FuneralPath';
import MemorialPath from '@/router/path/MemorialPath';
import AnnouncementPath from '@/router/path/AnnouncementPath';
import BusinessPath from '@/router/path/BusinessPath';
import MorePath from '@/router/path/MorePath';
import WellDyingPath from '@/router/path/WellDyingPath';
import DiaryPath from '@/router/path/DiaryPath';
import AndersenPath from '@/router/path/AndersenPath';
import { KEEP_ALIVE_OBJECT, SMILE_TYPE } from '@/common/constants/ConstCode';
import { notRegisteredRoutePath } from '@/router/guard/RouterFence';
import { URL_GUARD } from '@/common/utils/module/TextConst';
import { immigration } from '@/router/guard/RestrictPath';
import SmilePath from '@/router/path/SmilePath';
import CryPath from '@/router/path/CryPath';
import CardPath from '@/router/path/CardPath';
import FlyerPath from '@/router/path/FlyerPath';
import EventPath from '@/router/path/EventPath';
import ContentPath from '@/router/path/ContentPath';
import MembershipPath from '@/router/path/MembershipPath';
import { tokenRenewCheck } from '@/router/guard/TokenAuthentication';
import { businessGuard } from '@/router/guard/BusinessGuard';
// cry(추모관) FE 분해 Phase 1 — beforeEach 안 cry 전용 리다이렉트/헤더 로직을 cry-소유 guard 로 분리(동작보존).
import { cryRedirects, cryTypeLabel } from '@/router/guard/CryRouteGuard';

const routes = [
  {
    path: '/',
    name: 'Gateway',
    component: () => import('@/views/Root'),
    beforeEnter: () => {
      store.dispatch('common/getEnumData');
    }
  },
  {
    path: '/gate',
    name: 'ShareLinkGate',
    component: () => import('@/components/views/GatePage.vue')
  },
  {
    // 260616 방식2: 추천코드 공유 랜딩 — 코드 영속 저장 후 스토어/등록으로 라우팅(deferred 준비).
    //   설치된 앱은 /r/ App Link로 네이티브가 직접 처리(릴리스 후). 브라우저는 이 컴포넌트가 처리.
    path: '/r/:code',
    name: 'ReferralLanding',
    component: () => import('@/components/views/ReferralLanding.vue')
  },
  {
    // 260707 온라인 현판 QR 랜딩 — 공개(무가드) 스캔 진입점. 셸 숨김은 views/index.vue isQrPage.
    path: '/s/:code',
    name: 'QrLanding',
    component: () => import('@/components/views/qr/QrLanding.vue')
  },
  {
    path: '/s/:code/setup',
    name: 'QrOnboard',
    component: () => import('@/components/views/qr/QrOnboard.vue')
  },
  {
    // 260806 유저 — 가입·현판등록·전화인증·가게연결이 화면 4개로 흩어져 사장이 찾아다녀야 했다.
    //   순서를 정해주는 단일 진입점(허브). 랜딩과 같은 공개 경로 — 로그인은 1단계가 알아서 유도한다.
    path: '/s/:code/start',
    name: 'QrStart',
    component: () => import('@/components/views/qr/QrStart.vue')
  },
  {
    // 260708 관리 모드(경량관리 비번) — 랜딩 우상단 ≡로 진입
    path: '/s/:code/manage',
    name: 'QrManage',
    component: () => import('@/components/views/qr/QrManage.vue')
  },
  {
    // 260721 회의 — 오더판 대기 게임(룰렛·사다리). 랜딩과 동일 공개(무가드) 페이지, 셸 숨김은 isQrPage(/s/ prefix) 자동.
    path: '/s/:code/game',
    name: 'QrGame',
    component: () => import('@/components/views/qr/QrGamePage.vue')
  },
  ...MainPath,
  ...UserPath,
  ...FuneralPath,
  ...MemorialPath,
  ...AnnouncementPath,
  ...MorePath,
  ...WellDyingPath,
  ...DiaryPath,
  ...BusinessPath,
  ...SmilePath,
  ...CryPath,
  ...AndersenPath,
  ...CardPath,
  ...FlyerPath,
  ...EventPath,
  ...ContentPath,
  ...MembershipPath
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  mode: 'history',
  routes,
  scrollBehavior: (to, from, savedPosition) => {
    // 260524: 비즈니스 목록 ← 상세 뒤로가기 시 라우터가 top:200으로 끌어올리면
    // index.vue onActivated의 sessionStorage('biz-list-scroll') 복원이 무력화됨.
    // popstate(savedPosition truthy)면 router는 손 떼고 자체 복원 로직에 위임.
    if (to.path === '/business' && savedPosition) {
      return false;
    }
    return {
      el: document.getElementById('scroll'),
      behavior: 'smooth',
      top: 200
    };
  }
});

window.routeQuery = '';
window.path = '';

router.beforeEach(async (to, from, next) => {
  // 260521 호크룩스: 카카오 공유 link 진입점 /gate는 다른 모든 guard skip.
  //   guard chain 안에서 next()가 여러 번 호출되면 history stack에 /signin 등이
  //   비동기 race로 박힐 수 있어 폰별 분기 발생. /gate만 즉시 통과.
  if (to.path === '/gate' || to.path.startsWith('/r/') || to.path.startsWith('/s/')) {
    // /s/ = 온라인 현판 공개 랜딩(260707) — 비로그인 스캔이 본질이라 전체 guard skip
    return next();
  }

  // 260524 P0-2 (호크룩스 후속): /business onBeforeUnmount의 store wipe 제거하면서
  // /main → /business 진입 시 옛 검색 모드 cache 그대로 보임 (사용자 보고: '비즈니스 더보기 누르면 옛 카테고리로').
  // detail(/business/:no)에서 백키로 돌아오는 경우만 cache 유지, 그 외 경로에서 진입 시 store reset.
  if (to.path === '/business' && (!from.path || !from.path.startsWith('/business/'))) {
    try {
      store.commit('business/initBusinessList');
      store.commit('business/searchText');
      store.commit('business/setCategorySearchFlag');
      store.commit('layout/setBottomSheet', 'BUSINESS');
      store.commit('business/setOverseasFilter', false);
      // 260527: 홈 등 비-detail 경로에서 재진입 시 리스트 scrollTop도 맨 위로 리셋.
      // (detail→백키는 from이 /business/로 시작해 이 블록 skip → 보던 위치 복원 유지)
      sessionStorage.removeItem('biz-list-scroll');
    } catch (e) { /* ignore */ }
  }

  // 스마일 상세에서 행사 등록 페이지로 이동 시 (등록 후 뒤로가기) 메인으로 강제 이동

  if (from.path.includes('/smile/event/info/') && to.path.includes('/smile/event/form/')) {
    if (from.query.from === 'my') {
      let title = '';
      if (from.query.category === SMILE_TYPE.WEDDING.key) title = '온라인 결혼식 관리';
      else if (from.query.category === SMILE_TYPE.HUNDRED.key) title = '백일잔치/돌잔치 관리';
      else if (from.query.category === SMILE_TYPE.OLDER.key) title = '육순/칠순/팔순/구순/백세 관리';
      else if (from.query.category === SMILE_TYPE.HAPPY_BIRTH.key) title = '생일축하 관리';
      else if (from.query.category === SMILE_TYPE.OPEN_STORE.key) title = '개업식 관리';
      else if (from.query.category === SMILE_TYPE.CHRIST.key) title = '세례식 관리';

      next(`/smile/event/list?key=SMILE&header=${title}&category=${from.query.category}`);
    } else {
      next('/smile/event/menu');
    }
  }

  if (from.path.includes('/smile/invitation/info/') && to.path.includes('/smile/invitation/form/')) {
    next('/smile/invitation/menu');
  }

  if (from.path === '/' && to.path === '/signup/premium') {
    next('/signup');
  }

  // cry(추모관) 전용 강제 리다이렉트 3종 → CryRouteGuard.cryRedirects
  // (원본 제어흐름 그대로: next() 후 return 없이 fall-through — 첫 next 가 이기는 기존 동작 보존)
  cryRedirects(to, from, next);

  // 웰다잉 진입 시 로그인 여부 확인
  const user = store.getters['user/getUserData'];

  if (to.path.includes('welldying') && isEmpty(user.userNo)) {
    // 260521 호크룩스: history에 /signin 안 박히게 replace + return으로 후속 guard 중단
    return next({ path: '/signin', replace: true });
  }
  // /main 경로로 이동 시 쿼리스트링에 isRoot 추가
  if (to.path === '/main') to.query.isRoot = '1';

  let headerName = to.meta.titleName;

  // query.title - 쿼리스트링으로 전송 -  페이지별 , 선택자별 유동적으로 변화하는 헤더명을 설정하기 위함 ( 교회명 , 고인명 등등 )
  // meta.subName - router Path 에서 설정 -  페이지 단위로만 변하는 헤더명을 설정 ( ~~~ 추모관 ,~~~ 추모실 ,~~~ 장례식장 ,~~ Well Dying 등 )
  if (to.query.title) headerName = to.query.title;
  if (to.meta.subName && to.query.title) {
    headerName = `${to.query.title} ${cryTypeLabel()} ${to.meta.subName}`;
  }
  if (to.meta.subName && !to.query.title) {
    headerName = `${cryTypeLabel()} ${to.meta.subName}`;
  }
  to.meta.titleName = headerName;

  store.commit('layout/setIsFloating', false);

  window.routeQuery = to.query;
  window.path = to.path;

  await updateLayout(to, from, next);
  await immigration(to, from, next, router, [], []);

  // TODO login 완성시 해당 부분 url 변경 필요
  // if (!tokenRenewCheck(to)) return next('/signin'); // 토큰 만료시 로그인 페이지로 이동
  // if (!immigration(to, [], [])) return next('/main');
  if (!notRegisteredRoutePath(to, from, next)) return next('/main');
  await URL_GUARD(to.path);
  await tokenRenewCheck(to, next);
  await businessGuard(next);

  let component = [];

  if (isEmpty(store.getters['common/getEnum'])) {
    await store.dispatch('common/getEnumData');
  }

  const toExpression = to.meta.expression;
  const fromExpression = from.meta.expression;

  // Keep Alive 설정
  if (toExpression === fromExpression) {
    component = getKeepAliveComponent(toExpression, to, from);
  }
  store.commit('layout/keepingAlive', component);

  memorizeScroll();
  next();
});

router.afterEach((to, from, next) => {
  recoverScroll();
});

const getKeepAliveComponent = (expression, to, from) => {
  if (!isEmpty(expression)) {
    for (let key in KEEP_ALIVE_OBJECT) {
      if (KEEP_ALIVE_OBJECT[key].expression === expression) {
        return KEEP_ALIVE_OBJECT[key].list;
      }
    }
  }
  return []; // 일치하는 path가 없는 경우 빈 배열 반환 또는 다른 처리 방법 사용 가능
};

export default router;
